import { Routes, Route, Navigate } from 'react-router-dom';
import HomePage from '@/features/home/HomePage';
import { NotFoundPage } from '@/features/not-found/NotFoundPage';
import { RegisterPage } from '@/features/authentication/components/RegisterPage';
import {
  SubDelegateSignupPage,
  SubDelegateManagementPage,
} from '@/features/workspace-sub-delegation';
import { AuthCallback } from '@/features/authentication/components/AuthCallback';
import { PartnerCompanyLoginPage } from '@/features/authentication/components/PartnerCompanyLoginPage';
import { ProtectedRoute } from '@/core/router/ProtectedRoute';
import { AppLayout } from '@/core/layout';
import SimplifiedDashboard from '@/features/dashboard/care-seeker/components/SimplifiedDashboard';
import { ProviderDirectory } from '@/features/provider-directory/components/ProviderDirectory';
import { Dashboard as ServiceProviderDashboard } from '@/features/dashboard/service-provider';
import { Dashboard as FinancialAdvisorDashboard } from '@/features/dashboard/financial-advisor';
import { MyNetwork } from '@/features/network/components/MyNetwork';
import { MessagesPage } from '@/features/messaging/components/MessagesPage';
import { ChatbotPage } from '@/features/chatbot/components/ChatbotPage';
import { CareNavigatorPage } from '@/features/chatbot/care-navigator/CareNavigatorPage';
import { LatestDesignChatbotPage } from '@/features/chatbot/latest-design-components/ChatbotPage';
import { ProfilePage } from '@/features/profile/components/ProfilePage';
import { FavoritesPage } from '@/features/favorites/components/FavoritesPage';
import { ProviderDetails } from '@/features/provider-directory/components/ProviderDetails';
import { AdvisorProfileDetails } from '@/features/provider-directory/components/AdvisorProfileDetails';
import { AdminPortal } from '@/features/admin-portal/AdminPortal';
import { ProtectedRoute as AdminProtectedRoute } from '@/shared/components/ProtectedRoute';
import { MatchmakingRouter } from '@/features/matchmaking/components/MatchmakingRouter';
import { InvitationAcceptPage } from '@/pages/InvitationAcceptPage';
import { SelectAdvisorPage } from '@/features/partner-signup/pages/SelectAdvisorPage';
import { PrivacyPolicyPage } from '@/features/legal/pages/PrivacyPolicyPage';
import { TermsOfUsePage } from '@/features/legal/pages/TermsOfUsePage';
import { CareCostsPage } from '@/features/care-costs';
import { IntakeFormModalPrototypePage } from '@/features/provider-directory/components/prototype-intake-form/IntakeFormModalPrototypePage';
import { AdvisorConciergePage, CareConciergePage } from '@/features/care-concierge';
import {
  AdvisorReportPage,
  AssessmentResultsPage,
  LongevitySimulatorPage,
} from '@/features/longevity-assessment';
import { PartnerTrialPage, TrialEndedPage } from '@/features/partner-trial';
import {
  AuditEntryPage,
  RoomPage,
  ResultsPage,
} from '@/features/home-audit/components';
import {
  AuthAwareLayout,
  ChatbotAuthAwareLayout,
  PreLoginLandingOrRedirect,
  ResourceLibraryRoute,
  ResourceDetailRoute,
  LegacyPartnerSignupRedirect,
  SignupToLandingRedirect,
  PartnerSignupToLandingRedirect,
  RoleAwareDashboardRoute,
  LongevityTakeRoute,
  FeedbackRoute,
} from './route-helpers';

export const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<HomePage />} />
      <Route path="/login/:companySlug" element={<PartnerCompanyLoginPage />} />

      {/* Pre-login shell routes — public for guests, AppLayout for authenticated users.
          Navigator (/matchmaking) is NOT public — it's a paid AI service. Guests see a
          locked tab in PreLoginShell; the route itself lives in the ProtectedRoute block. */}
      <Route element={<AuthAwareLayout />}>
        <Route path="/" element={<PreLoginLandingOrRedirect />} />
        <Route path="/search-providers" element={<ProviderDirectory />} />
        {/* Feature 076-03: SP profiles are public — guests see full content; Message /
            Request Consultation / Save gate to the signup modal. FA profiles
            (/advisors/:id) stay in the ProtectedRoute block. */}
        <Route path="/providers/:id" element={<ProviderDetails />} />
        <Route path="/resources" element={<ResourceLibraryRoute />} />
        {/* Spec Item 2: resource detail accessible to guests for views 1-2; the 3rd hit is
            intercepted on the library card click (ResourceCardPrototype) before navigation. */}
        <Route path="/resource-hub-detail/:seoUrl" element={<ResourceDetailRoute />} />
      </Route>
      <Route path="/register" element={<RegisterPage />} />
      {/* Spec Item 1: /signup and variants redirect to / preserving referral code / invite token */}
      {/* T028: SP/FP invite signup still works via InviteSignupPage on /sp-invitations/:token */}
      <Route path="/signup/:inviteId" element={<SignupToLandingRedirect />} />
      <Route path="/signup" element={<SignupToLandingRedirect />} />
      {/* Workspace Sub-Delegation — Feature 075 (public; page branches on auth state) */}
      <Route path="/sub-delegate-signup" element={<SubDelegateSignupPage />} />
      <Route path="/invite/:code" element={<Navigate to="/" replace />} />
      <Route path="/invite" element={<Navigate to="/" replace />} />

      {/* Feature 095: anonymous Longevity Readiness Assessment (token link) */}
      <Route path="/longevity-assessment/:token" element={<LongevityTakeRoute />} />
      {/* Public longevity simulator for training-course FPs (course link `?training`;
          no auth guard — logged-in visitors get the same anonymous flow). */}
      <Route path="/longevity-simulator" element={<LongevitySimulatorPage />} />

      {/* Feature 102: anonymous referral-feedback form (email token link) */}
      <Route path="/feedback/:token" element={<FeedbackRoute />} />

      {/* Legal Pages - Public routes for Privacy Policy and Terms of Use */}
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-use" element={<TermsOfUsePage />} />

      {/* Public pages with nav bar — CareScout Cost of Care */}
      <Route element={<AppLayout />}>
        <Route path="/care-costs" element={<CareCostsPage />} />
        {/* Public, login-optional Care Concierge intake — same form as the in-app
            modal; guests submit via the reCAPTCHA guest-session path. */}
        <Route path="/care-concierge" element={<CareConciergePage />} />
        {/* Feature 104: public FP-specific intake (form only, login-optional) */}
        <Route path="/care-concierge/advisors" element={<AdvisorConciergePage />} />
        {/* Feature 100: public FP trial-request landing page */}
        <Route path="/partner-trial" element={<PartnerTrialPage />} />
        <Route path="/trial-ended" element={<TrialEndedPage />} />
      </Route>

      {/* Care Navigator — public for guests (bot-gated + session-scoped backend),
          AppLayout for authenticated users. Account-only actions pop the signup modal.
          `/care-navigator` is the new 2-step (role-aware) flow; the original 5-step
          flow now lives at `/detailed-care-navigator`. */}
      <Route element={<ChatbotAuthAwareLayout />}>
        <Route path="/care-navigator/*" element={<CareNavigatorPage />} />
        <Route path="/detailed-care-navigator/*" element={<ChatbotPage />} />
        <Route path="/redesign-chatbot/*" element={<LatestDesignChatbotPage />} />
      </Route>

      {/* Back-compat: legacy /chatbot links redirect to the rebranded route. */}
      <Route path="/chatbot/*" element={<Navigate to="/care-navigator" replace />} />

      {/* Spec Item 1: /partner-signup redirects to / preserving referral code */}
      <Route path="/partner-sign-up" element={<LegacyPartnerSignupRedirect />} />
      <Route path="/partner-signup" element={<PartnerSignupToLandingRedirect />} />

      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* SP Client Invitation Accept Page - Feature 001: Public route for accepting invitations */}
      <Route path="/sp-invitations/:token" element={<InvitationAcceptPage />} />

      {/* Admin Portal Routes (T043 - US1) — inside AppLayout for the app sidebar */}
      <Route element={<AppLayout />}>
        <Route
          path="/admin/*"
          element={
            <AdminProtectedRoute requireAdmin>
              <AdminPortal />
            </AdminProtectedRoute>
          }
        />
      </Route>

      {/* Dashboard Router - redirects to role-appropriate dashboard */}
      <Route path="/my" element={<RoleAwareDashboardRoute />} />
      <Route path="/dashboard" element={<RoleAwareDashboardRoute />} />

      {/* Advisor Selection - Feature 038: Protected route for advisor selection during partner signup */}
      {/* T050 - US3: Auth-guarded page with custom layout (green background, no topbar) */}
      <Route
        path="/select-advisor"
        element={
          <ProtectedRoute>
            <SelectAdvisorPage />
          </ProtectedRoute>
        }
      />

      {/* Feature 095: authed Longevity results report (client + advisor) */}
      <Route
        path="/longevity-assessment/results/:id"
        element={
          <ProtectedRoute>
            <AssessmentResultsPage />
          </ProtectedRoute>
        }
      />

      {/* Feature 110: the advisor's planning view of an MVP 2 result. Separate
          from the client report because it holds the advisor's own prompts —
          the service refuses it to anyone but the advisor who sent it. */}
      <Route
        path="/longevity-assessment/advisor-report/:id"
        element={
          <ProtectedRoute>
            <AdvisorReportPage />
          </ProtectedRoute>
        }
      />

      {/* Protected Routes with AppLayout (includes Topbar) */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard Routes */}
        <Route path="/dashboard/care-seeker" element={<SimplifiedDashboard />} />

        <Route
          path="/dashboard/service-provider"
          element={<ServiceProviderDashboard />}
        />

        <Route
          path="/dashboard/financial-advisor"
          element={<FinancialAdvisorDashboard />}
        />

        <Route
          path="/dashboard/admin"
          element={
            <div className="p-8">
              <h1 className="text-2xl font-bold">Admin Dashboard</h1>
              <p className="mt-4 text-gray-600">Dashboard content coming soon...</p>
            </div>
          }
        />

        {/* DEPRECATED (Feature 011): Onboarding and journey routes redirect to simplified dashboard */}
        <Route path="/onboarding" element={<Navigate to="/dashboard/care-seeker" replace />} />
        <Route path="/journey" element={<Navigate to="/dashboard/care-seeker" replace />} />
        <Route path="/journey/:step" element={<Navigate to="/dashboard/care-seeker" replace />} />

        {/* Provider Directory - Phase 7: US4 - Provider Directory & Search.
            /providers/:id moved to the AuthAwareLayout block (feature 076-03). */}
        <Route path="/advisors/:id" element={<AdvisorProfileDetails />} />

        {/* Navigator (AI matchmaking) — authenticated only. */}
        <Route path="/matchmaking" element={<MatchmakingRouter />} />

        <Route path="/messages" element={<MessagesPage />} />

        {/* Care Seeker Routes */}
        {/* Feature 017: My Team (renamed from Favorites) */}
        <Route path="/my-team" element={<FavoritesPage />} />

        {/* Service Provider Routes */}
        <Route
          path="/profile-management"
          element={
            <div className="p-8">
              <h1 className="text-2xl font-bold">Profile Management</h1>
              <p className="mt-4 text-gray-600">Profile management feature coming soon...</p>
            </div>
          }
        />

        <Route
          path="/leads"
          element={
            <div className="p-8">
              <h1 className="text-2xl font-bold">Engagements</h1>
              <p className="mt-4 text-gray-600">Engagement management coming soon...</p>
            </div>
          }
        />

        {/* Financial Advisor Routes */}
        <Route
          path="/client-activity"
          element={
            <div className="p-8">
              <h1 className="text-2xl font-bold">Client Activity</h1>
              <p className="mt-4 text-gray-600">Client activity tracking coming soon...</p>
            </div>
          }
        />

        <Route
          path="/smart-search"
          element={
            <div className="p-8">
              <h1 className="text-2xl font-bold">Smart Search</h1>
              <p className="mt-4 text-gray-600">AI-powered search agent coming soon...</p>
            </div>
          }
        />

        <Route
          path="/analytics"
          element={
            <div className="p-8">
              <h1 className="text-2xl font-bold">Analytics</h1>
              <p className="mt-4 text-gray-600">Analytics dashboard coming soon...</p>
            </div>
          }
        />

        {/* Common Routes - All authenticated users */}
        <Route path="/my/network" element={<Navigate to="/network" replace />} />
        <Route path="/network" element={<MyNetwork />} />

        {/* Resource detail now lives in the AuthAwareLayout block (spec Item 2). */}

        {/* Home Safety Audit - Phase 3 */}
        <Route path="/home-audit" element={<AuditEntryPage />} />
        <Route path="/home-audit/results" element={<ResultsPage />} />
        <Route path="/home-audit/:room" element={<RoomPage />} />

        {/* User Profile - Phase 14: US11 - User Profile & Settings */}
        <Route path="/profile" element={<ProfilePage />} />

        {/* Workspace Sub-Delegation — Feature 075: owner self-service management */}
        <Route
          path="/workspace/sub-delegates"
          element={<SubDelegateManagementPage />}
        />
        
        <Route 
          path="/settings" 
          element={
            <div className="p-8">
              <h1 className="text-2xl font-bold">Settings</h1>
              <p className="mt-4 text-gray-600">Account settings coming soon...</p>
            </div>
          }
        />
      </Route>

      {/* PROTOTYPE — throwaway, wayfinder ticket 10 (103-sp-embedded-intake-form). Dev-only, drop with the folder. */}
      {import.meta.env.DEV && (
        <Route path="/prototype/intake-form-modal" element={<IntakeFormModalPrototypePage />} />
      )}

      {/* Error Pages */}
      <Route path="/404" element={<NotFoundPage />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
};
